/**
 * "Card entry only" — asked for by the URL the guest arrived on, remembered for the rest of the visit.
 *
 * The handheld now offers "Pay here with your credit card" on its QR screen, which opens THIS app in an
 * Android WebView on the tablet rather than on the guest's phone. Apple Pay needs Safari and Google Pay for
 * web needs an integration a plain WebView does not provide, so both wallet buttons are dead there. The
 * tablet appends `?cardOnly=true` and we show the card path alone.
 *
 * GATED SOLELY ON THE PARAM, never inferred from the user agent. The QR code still encodes the plain link, so
 * a guest who scans it with their own phone must keep their wallets - and a UA check would take them away
 * from anyone whose browser merely looked like a WebView.
 *
 * WHY THIS IS STORED RATHER THAN READ FROM THE URL WHERE IT IS NEEDED. The link is `/c/{code}`, which
 * `ScanRoute` trades for a session and leaves immediately for `/check`; the query string does not survive
 * that redirect, let alone the four navigations after it or the trip out to a hosted payment page and back.
 * The session is persisted for exactly the same reason - see `session/storage.ts`.
 */
const STORAGE_KEY = 'touchtab.guest-card-only';

/** The name the handheld appends. Agreed with TouchTab.Mobile.Restaurant - see docs/request-cardonly-tablet-mode.md. */
export const CARD_ONLY_PARAM = 'cardOnly';

/**
 * Whether a URL is asking for card entry only.
 *
 * Exactly `true` and nothing else. The spec is explicit that any other value reads as absent, and being
 * strict here is what keeps a typo or a truthiness trick from quietly taking a guest's wallets away.
 */
export function readsAsCardOnly(search: string): boolean {
    try {
        return new URLSearchParams(search).get(CARD_ONLY_PARAM) === 'true';
    } catch {
        return false;
    }
}

/**
 * Records the answer for this visit.
 *
 * Called with the answer either way, not only when it is true. A tablet is a shared device: the same WebView
 * on the same origin will open a different check tomorrow, and a `true` left behind from last night would
 * suppress wallets for a guest whose link never asked for it. Writing `false` on a plain arrival is what
 * makes the flag expire with the visit that set it.
 */
export function setCardOnly(cardOnly: boolean): void {
    try {
        if (cardOnly) localStorage.setItem(STORAGE_KEY, 'true');
        else localStorage.removeItem(STORAGE_KEY);
    } catch {
        // Private browsing, or storage full. Degrades to showing the wallets, which on the tablet means two
        // buttons that do nothing rather than a guest who cannot pay - see `saveSession` for the same trade.
    }

    // Kept in step with the flag, not only applied at startup. The first arrival writes the flag AFTER the
    // app has booted - ScanRoute is what reads the URL - so a widening applied only on load would miss the
    // very visit that asked for it and take effect on the next one.
    applyTabletShell(cardOnly);
}

export function isCardOnly(): boolean {
    try {
        return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
        return false;
    }
}

/**
 * How wide the app is allowed to get on the tablet.
 *
 * Wider than a phone and nowhere near the whole screen. The receipt's line items, the five-face rating row
 * and the tip options were all composed against a narrow column; stretched across a 1200px WebView they read
 * as broken rather than as roomy.
 *
 * In px rather than rem on purpose, so it stays put when the root font size below changes. Two knobs that
 * move independently are easier to tune than two that multiply.
 */
const TABLET_SHELL_WIDTH = '768px';

/**
 * How big everything gets on the tablet: text, buttons, padding, gaps, corner radii.
 *
 * ONE NUMBER FOR ALL OF IT, because this app is sized in rem throughout - Tailwind's `--spacing` is
 * 0.25rem, so `min-h-12`, `size-14`, `px-5` and `gap-4` are all multiples of the root font size, as are the
 * text scales. Moving the root moves them together and in proportion, which is the only way to make things
 * bigger without re-tuning every screen and discovering later that one button did not come with them.
 *
 * 20px against the browser's 16px default, so a quarter larger. A phone is held at about 35cm and a tablet
 * on a stand is read at arm's length; the same 48px button that is generous in the hand is small across a
 * table. The docked footer keeps its clearance for free, because the padding that reserves it is rem too.
 *
 * Not applied on a phone, where the guest's own browser text-size setting should be the only thing deciding
 * this.
 */
const TABLET_ROOT_FONT_SIZE = '20px';

/**
 * How much to magnify the gateway's card form on the tablet.
 *
 * THE ONE PLACE THE ROOT FONT SIZE ABOVE CANNOT REACH. The card fields are AurusPay's hosted page in a
 * cross-origin iframe, so no stylesheet of ours applies to them - that is the browser's boundary, not a gap
 * here. `zoom` on the iframe ELEMENT works anyway, because the parent never touches the child's document:
 * it is compositing, not styling.
 *
 * Magnification rather than design, and it shows: the inner page reflows to a viewport 1/1.3 as wide, so
 * the fields get bigger but may also wrap differently than PAR intended. The proper fix is a hosted-page
 * template with larger type, chosen by the `TemplateId` we send when staging the session - see
 * `AurusPayModels.SessionRequest` in TouchTab.ParPayAPI. This is the stopgap until that exists, and should
 * be deleted when it does.
 */
export const TABLET_GATEWAY_ZOOM = 1.3;

/**
 * The same, for a guest on their own phone, where PAR's fields are also small - just less catastrophically.
 *
 * Gentler than the tablet's, and not out of caution about the technique. A phone has no width to spare:
 * zoom divides the viewport the hosted page lays itself out against, so magnifying costs PAR room they may
 * need. 1.3 on a 390px phone would leave them 300px, which is below what most responsive forms are built
 * for. 1.15 leaves 339px.
 */
export const PHONE_GATEWAY_ZOOM = 1.15;

/**
 * The narrowest viewport we will leave the hosted page.
 *
 * 320px is the floor essentially every responsive design still supports, and PAR's is not ours to test. On
 * a phone small enough that magnifying would push them below it, the honest answer is not to magnify: a
 * guest who cannot read the fields is better off than one whose form has collapsed.
 */
const MIN_GATEWAY_VIEWPORT = 320;

/**
 * How much to magnify the gateway frame, given the room there is to do it in.
 *
 * @param available
 * The width the guard is judged against, defaulting to the viewport. On a phone that IS the frame - it runs
 * full-bleed inside the shell. On the tablet the frame is narrower than the viewport, at the shell's 768px,
 * so the guard there is measured against more room than the frame has. That is deliberate and safe rather
 * than sloppy: 768 still affords 2.4x and the tablet only asks for 1.3, so the floor is nowhere near. If
 * either number ever moves far enough for that to stop being true, measure the frame instead of assuming.
 */
export function gatewayZoom(available: number = window.innerWidth): number {
    const wanted = isCardOnly() ? TABLET_GATEWAY_ZOOM : PHONE_GATEWAY_ZOOM;

    // What this width can afford before the inner page drops under the floor. Never below 1: shrinking
    // PAR's page to buy it room is not a trade anybody asked for.
    const affordable = Math.max(1, available / MIN_GATEWAY_VIEWPORT);

    return Math.min(wanted, affordable);
}

/**
 * Sizes the shell for the tablet, or puts it back.
 *
 * REMOVES the properties rather than writing the phone values back, so the stylesheet default is what takes
 * over - the same rule `applySkin` follows, and for the same reason: blanking would break the fallback chain
 * instead of engaging it.
 */
function applyTabletShell(cardOnly: boolean, root: HTMLElement = document.documentElement): void {
    if (cardOnly) {
        root.style.setProperty('--shell-max-width', TABLET_SHELL_WIDTH);
        root.style.fontSize = TABLET_ROOT_FONT_SIZE;
        return;
    }

    root.style.removeProperty('--shell-max-width');
    root.style.removeProperty('font-size');
}

/**
 * Applies the stored answer at startup.
 *
 * Every load after the first arrives with the flag already in storage and no `?cardOnly=true` in the URL to
 * re-read - a navigation between steps, a reload, or the return from a hosted payment page. Without this the
 * tablet would size itself once and then snap back to phone proportions on the way to the receipt.
 */
export function applyStoredTabletShell(): void {
    applyTabletShell(isCardOnly());
}
